/* eslint-disable no-restricted-syntax */
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { statsService } from 'utils/api';
import { useSelector } from 'react-redux';
import session from 'containers/App/session';
import dayjs from 'utils/dayjs';
import { isEmpty, isEqual } from 'lodash';
import { Affix, Col, Collapse, Flex, Modal, Row, Tag } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import Graph from './Graph';
import { X_AXIS_TYPE, Y_AXIS_TYPE, CHART_TYPE, TOTAL_TYPE, REVFLOW_REPORT } from './constants';
import StatsHeader from './StatsHeader';
import { RangePicker } from 'components/DatePicker';
import FlipGraph from './components/FlipGraph';
import Button from 'components/forms/Button';
import { downloadExcel } from 'utils/fileDownload';

const Container = styled.div`
    overflow: auto;
    padding: 20px;
    min-width: 900px;
    > * {
        margin-bottom: 15px;
    }
`;

const GraphContainer = styled.div`
    margin: 10px;
    padding: 10px;
    border-radius: 20px;
    border: 1px solid #dddddd;
    height: 100%;
`;

const RevFlow = () => {
    const [fetching, setFetching] = useState(false);
    const [fetched, setFetched] = useState(true);
    const [fetchingSpreadsheet, setFetchingSpreadsheet] = useState(false);
    const [allData, setAllData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [filters, setFilters] = useState([]);
    const propertyId = useSelector(session.selectors.selectSelectedPropertyId);
    const currency = useSelector(session.selectors.selectCurrency);
    const propertyName = useSelector(session.selectors.selectPropertyName);
    const token = useSelector(session.selectors.selectUserToken);
    const startDate = dayjs().subtract(1, 'year');
    const endDate = dayjs().add(1, 'year');
    const [selectedStartDate, setSelectedStartDate] = useState(dayjs().startOf('year'));
    const [selectedEndDate, setSelectedEndDate] = useState(dayjs().endOf('year'));
    const [modal, contextHolder] = Modal.useModal();

    const possiblyAddFilter = (filter) => {
        if (!filters.some((f) => f.property === filter.property && f.equals === filter.equals)) {
            setFilters([...filters, filter]);
        }
    };

    const filterData = () => {
        setFilteredData(
            allData.filter(
                (d) =>
                    dayjs(d.stayDate).isSameOrAfter(selectedStartDate, 'day') &&
                    dayjs(d.stayDate).isSameOrBefore(selectedEndDate, 'day'),
            ),
        );
    };

    const loadData = async () => {
        setFetching(true);
        setFetched(false);
        const instance = modal.info({
            title: 'Loading',
            content: `Currently loading 2 years of data - please don't refresh your browser while data is loading. This
                    can take up to a minute`,
        });
        const propertieInGroupResponse = await statsService({
            token,
            propertyId,
        }).getPropertiesInGroup();
        const thisProperty = propertieInGroupResponse.data.find((p) => p.name === propertyName);
        const response = await statsService({
            token,
            propertyId,
        }).getRevFlow({
            startDate: startDate.format('YYYY-MM-DD'),
            endDate: endDate.format('YYYY-MM-DD'),
            lastYearComparison: 'DATE',
        });
        if (response.data[thisProperty.id]?.data) {
            const data = response.data[thisProperty.id]?.data;
            setAllData(Object.entries(data).map(([key, value]) => ({ ...value, stayDate: key })));
        }

        setFetching(false);
        setFetched(true);
        instance.destroy();
    };

    const exportData = async (reportType) => {
        setFetchingSpreadsheet(true);
        try {
            const excelFileResponse = await statsService({
                token,
                propertyId,
            }).getRevFlowReport({
                startDate: selectedStartDate.format('YYYY-MM-DD'),
                endDate: selectedEndDate.format('YYYY-MM-DD'),
                lastYearComparison: 'DATE',
                reportType,
                filters,
            });

            const contentDisposition = excelFileResponse.headers['content-disposition'];
            const match = contentDisposition.match(/filename\s*=\s*"(.+)"/i);
            const filename = match ? match[1] : 'export.xlsx';
            downloadExcel(excelFileResponse.data, filename);
        } catch (error) {
            Modal.error({
                title: 'Export Error',
                content: 'There was an error exporting the data. Please try again later.',
            });
        }

        setFetchingSpreadsheet(false);
    };

    const removeFilter = (filter) => {
        setFilters([...filters.filter((f) => !isEqual(f, filter))]);
    };

    const preventDefault = (e, filter) => {
        e.preventDefault();
        removeFilter(filter);
    };

    const clearFilters = () => {
        setFilters([]);
    };

    const filterTagRender = (filter) => (
        <Tag
            color="blue"
            closable
            onClose={(e) => preventDefault(e, filter)}
            style={{
                fontSize: '14px',
                padding: '5px',
            }}
        >
            {!isEmpty(filter.label) && `${filter.label}: `}
            {filter.equals}
        </Tag>
    );

    const clearAllTagRender = () => (
        <Tag
            color="red"
            closable
            onClose={() => clearFilters()}
            style={{
                fontSize: '14px',
                padding: '5px',
            }}
        >
            Clear all
        </Tag>
    );

    const onChangeDates = (dates) => {
        if (!dates) {
            setSelectedStartDate(dayjs().startOf('year'));
            setSelectedEndDate(dayjs().endOf('year'));
        } else {
            setSelectedStartDate(dates[0]);
            setSelectedEndDate(dates[1]);
        }
    };

    useEffect(() => {
        if (!isEmpty(allData)) {
            filterData();
        }
    }, [selectedStartDate, selectedEndDate, allData]);

    useEffect(() => {
        setFilters([]);
        loadData();
    }, [propertyId]);

    return (
        <>
            {contextHolder}
            <Affix offsetTop={80}>
                <Collapse
                    defaultActiveKey={['1']}
                    style={{ margin: 0, padding: 0, position: 'sticky', top: 80, background: '#f3f3f3' }}
                    size="small"
                    bordered={false}
                    ghost
                    items={[
                        {
                            style: { padding: 0, margin: 0 },
                            key: '1',
                            children: (
                                <StatsHeader
                                    data={filteredData}
                                    filters={filters}
                                    currency={currency}
                                    fetching={fetching}
                                    fetched={fetched}
                                />
                            ),
                        },
                    ]}
                />
            </Affix>
            <Container>
                <Row justify="space-between" align="middle">
                    <Col flex="auto">
                        <div style={{ textAlign: 'center' }}>
                            <RangePicker
                                minDate={startDate}
                                maxDate={endDate}
                                onChange={onChangeDates}
                                style={{ marginRight: '5px' }}
                            />
                            {selectedStartDate.format('DD MMM YYYY')} - {selectedEndDate.format('DD MMM YYYY')}
                        </div>
                    </Col>
                    <Col>
                        <Button onClick={() => exportData()} loading={fetchingSpreadsheet} disabled={fetching}>
                            <DownloadOutlined />
                        </Button>
                    </Col>
                </Row>
                <Flex gap="4px 0" wrap>
                    {filters.map((filter) => filterTagRender(filter))}
                    {!isEmpty(filters) && clearAllTagRender()}
                </Flex>
                <Row>
                    <Col span="8">
                        <GraphContainer>
                            <Button
                                onClick={() => exportData(REVFLOW_REPORT.REVENUE)}
                                loading={fetchingSpreadsheet}
                                disabled={fetching}
                                style={{ position: 'absolute', top: '20px', right: '20px', zIndex: '5' }}
                                size="small"
                            >
                                <DownloadOutlined />
                            </Button>
                            <FlipGraph
                                Graph1={{
                                    optionName: 'Month',
                                    graph: (
                                        <Graph
                                            key="revenue"
                                            data={filteredData}
                                            possiblyAddFilter={possiblyAddFilter}
                                            removeFilter={removeFilter}
                                            filters={filters}
                                            grouping="MMM"
                                            xAxisType={X_AXIS_TYPE.DATE}
                                            yAxisType={Y_AXIS_TYPE.CURRENCY}
                                            currency={currency}
                                            filterProperty="stayDate"
                                            filterDateFormat="MMM"
                                            fetched={fetched}
                                            fetching={fetching}
                                            graphTitle="Revenue by month"
                                            yAxisLabel="Revenue"
                                            lySpitPathToData="lySpit.stats.guaranteed.revenue.accomodation.net"
                                            lyFinalPathToData="lyFinal.stats.guaranteed.revenue.accomodation.net"
                                            budgetPathToData="budget.revenue.accommodation"
                                            series={[
                                                {
                                                    pathToData: 'current.stats.guaranteed.revenue.accomodation.net',
                                                    legendName: 'Guar',
                                                },
                                                {
                                                    pathToData: 'current.stats.unguaranteed.revenue.accomodation.net',
                                                    legendName: 'Unguar',
                                                },
                                            ]}
                                            chartType={CHART_TYPE.BAR}
                                            exportData
                                            fetchingSpreadsheet
                                        />
                                    ),
                                }}
                                Graph2={{
                                    optionName: 'Day',
                                    graph: (
                                        <Graph
                                            key="revenueDayOfWeek"
                                            data={filteredData}
                                            possiblyAddFilter={possiblyAddFilter}
                                            removeFilter={removeFilter}
                                            filters={filters}
                                            grouping="ddd"
                                            xAxisType={X_AXIS_TYPE.DATE}
                                            yAxisType={Y_AXIS_TYPE.CURRENCY}
                                            currency={currency}
                                            filterProperty="stayDate"
                                            filterDateFormat="ddd"
                                            fetched={fetched}
                                            fetching={fetching}
                                            graphTitle="Revenue by day of week"
                                            yAxisLabel="Revenue"
                                            lySpitPathToData="lySpit.stats.guaranteed.revenue.accomodation.net"
                                            lyFinalPathToData="lyFinal.stats.guaranteed.revenue.accomodation.net"
                                            budgetPathToData="budget.revenue.accommodation"
                                            series={[
                                                {
                                                    pathToData: 'current.stats.guaranteed.revenue.accomodation.net',
                                                    legendName: 'Guar',
                                                },
                                                {
                                                    pathToData: 'current.stats.unguaranteed.revenue.accomodation.net',
                                                    legendName: 'Unguar',
                                                },
                                            ]}
                                            chartType={CHART_TYPE.BAR}
                                        />
                                    ),
                                }}
                            />
                        </GraphContainer>
                    </Col>
                    <Col span="8">
                        <GraphContainer>
                            <Button
                                onClick={() => exportData(REVFLOW_REPORT.ROOMS)}
                                loading={fetchingSpreadsheet}
                                disabled={fetching}
                                style={{ position: 'absolute', top: '20px', right: '20px', zIndex: '5' }}
                                size="small"
                            >
                                <DownloadOutlined />
                            </Button>
                            <FlipGraph
                                Graph1={{
                                    optionName: 'Month',
                                    graph: (
                                        <Graph
                                            key="rooms"
                                            data={filteredData}
                                            possiblyAddFilter={possiblyAddFilter}
                                            removeFilter={removeFilter}
                                            filters={filters}
                                            grouping="MMM"
                                            xAxisType={X_AXIS_TYPE.DATE}
                                            filterProperty="stayDate"
                                            filterDateFormat="MMM"
                                            fetched={fetched}
                                            fetching={fetching}
                                            graphTitle="Rooms by month"
                                            yAxisLabel="Rooms"
                                            lySpitPathToData="lySpit.stats.guaranteed.rooms"
                                            lyFinalPathToData="lyFinal.stats.guaranteed.rooms"
                                            budgetPathToData="budget.rooms"
                                            series={[
                                                {
                                                    pathToData: 'current.stats.guaranteed.rooms',
                                                    legendName: 'Guar',
                                                },
                                                {
                                                    pathToData: 'current.stats.unguaranteed.rooms',
                                                    legendName: 'Unguar',
                                                },
                                            ]}
                                            chartType={CHART_TYPE.BAR}
                                        />
                                    ),
                                }}
                                Graph2={{
                                    optionName: 'Day',
                                    graph: (
                                        <Graph
                                            key="roomDayOfWeek"
                                            data={filteredData}
                                            possiblyAddFilter={possiblyAddFilter}
                                            removeFilter={removeFilter}
                                            filters={filters}
                                            grouping="ddd"
                                            xAxisType={X_AXIS_TYPE.DATE}
                                            filterProperty="stayDate"
                                            filterDateFormat="ddd"
                                            fetched={fetched}
                                            fetching={fetching}
                                            graphTitle="Rooms by day of week"
                                            yAxisLabel="Rooms"
                                            lySpitPathToData="lySpit.stats.guaranteed.rooms"
                                            lyFinalPathToData="lyFinal.stats.guaranteed.rooms"
                                            budgetPathToData="budget.rooms"
                                            series={[
                                                {
                                                    pathToData: 'current.stats.guaranteed.rooms',
                                                    legendName: 'Guar',
                                                },
                                                {
                                                    pathToData: 'current.stats.unguaranteed.rooms',
                                                    legendName: 'Unguar',
                                                },
                                            ]}
                                            chartType={CHART_TYPE.BAR}
                                        />
                                    ),
                                }}
                            />
                        </GraphContainer>
                    </Col>
                    <Col span="8">
                        <GraphContainer>
                            <Button
                                onClick={() => exportData(REVFLOW_REPORT.ADR)}
                                loading={fetchingSpreadsheet}
                                disabled={fetching}
                                style={{ position: 'absolute', top: '20px', right: '20px', zIndex: '5' }}
                                size="small"
                            >
                                <DownloadOutlined />
                            </Button>
                            <FlipGraph
                                Graph1={{
                                    optionName: 'Month',
                                    graph: (
                                        <Graph
                                            key="adrMonth"
                                            data={filteredData}
                                            possiblyAddFilter={possiblyAddFilter}
                                            removeFilter={removeFilter}
                                            filters={filters}
                                            grouping="MMM"
                                            xAxisType={X_AXIS_TYPE.DATE}
                                            yAxisType={Y_AXIS_TYPE.CURRENCY}
                                            currency={currency}
                                            filterProperty="stayDate"
                                            filterDateFormat="MMM"
                                            fetched={fetched}
                                            fetching={fetching}
                                            graphTitle="ADR by month"
                                            yAxisLabel="ADR"
                                            lySpitPathToData="lySpit.stats.adr.net"
                                            lyFinalPathToData="lyFinal.stats.adr.net"
                                            budgetPathToData="budget.adr"
                                            series={[
                                                {
                                                    pathToData: 'current.stats.adr.net',
                                                    legendName: 'ADR',
                                                },
                                            ]}
                                            chartType={CHART_TYPE.BAR}
                                            totalType={TOTAL_TYPE.AVERAGE}
                                        />
                                    ),
                                }}
                                Graph2={{
                                    optionName: 'Day',
                                    graph: (
                                        <Graph
                                            key="adrDayOfWeek"
                                            data={filteredData}
                                            possiblyAddFilter={possiblyAddFilter}
                                            removeFilter={removeFilter}
                                            filters={filters}
                                            grouping="ddd"
                                            xAxisType={X_AXIS_TYPE.DATE}
                                            yAxisType={Y_AXIS_TYPE.CURRENCY}
                                            currency={currency}
                                            filterProperty="stayDate"
                                            filterDateFormat="ddd"
                                            fetched={fetched}
                                            fetching={fetching}
                                            graphTitle="ADR by day of week"
                                            yAxisLabel="ADR"
                                            lySpitPathToData="lySpit.stats.adr.net"
                                            lyFinalPathToData="lyFinal.stats.adr.net"
                                            budgetPathToData="budget.adr"
                                            series={[
                                                {
                                                    pathToData: 'current.stats.adr.net',
                                                    legendName: 'ADR',
                                                },
                                            ]}
                                            chartType={CHART_TYPE.BAR}
                                            totalType={TOTAL_TYPE.AVERAGE}
                                        />
                                    ),
                                }}
                            />
                        </GraphContainer>
                    </Col>
                </Row>
                <Row>
                    <Col span="8">
                        <GraphContainer>
                            <Button
                                onClick={() => exportData(REVFLOW_REPORT.REVPAR)}
                                loading={fetchingSpreadsheet}
                                disabled={fetching}
                                style={{ position: 'absolute', top: '20px', right: '20px', zIndex: '5' }}
                                size="small"
                            >
                                <DownloadOutlined />
                            </Button>
                            <FlipGraph
                                Graph1={{
                                    optionName: 'Month',
                                    graph: (
                                        <Graph
                                            key="revParMonth"
                                            data={filteredData}
                                            possiblyAddFilter={possiblyAddFilter}
                                            removeFilter={removeFilter}
                                            filters={filters}
                                            grouping="MMM"
                                            xAxisType={X_AXIS_TYPE.DATE}
                                            yAxisType={Y_AXIS_TYPE.CURRENCY}
                                            currency={currency}
                                            filterProperty="stayDate"
                                            filterDateFormat="MMM"
                                            fetched={fetched}
                                            fetching={fetching}
                                            graphTitle="RevPAR by month"
                                            yAxisLabel="RevPAR"
                                            lySpitPathToData="lySpit.stats.revPar.net"
                                            lyFinalPathToData="lyFinal.stats.revPar.net"
                                            budgetPathToData="budget.revPar"
                                            series={[
                                                {
                                                    pathToData: 'current.stats.revPar.net',
                                                    legendName: 'RevPAR',
                                                },
                                            ]}
                                            chartType={CHART_TYPE.BAR}
                                            totalType={TOTAL_TYPE.AVERAGE}
                                        />
                                    ),
                                }}
                                Graph2={{
                                    optionName: 'Day',
                                    graph: (
                                        <Graph
                                            key="revenueDayOfWeek"
                                            data={filteredData}
                                            possiblyAddFilter={possiblyAddFilter}
                                            removeFilter={removeFilter}
                                            filters={filters}
                                            grouping="ddd"
                                            xAxisType={X_AXIS_TYPE.DATE}
                                            yAxisType={Y_AXIS_TYPE.CURRENCY}
                                            currency={currency}
                                            filterProperty="stayDate"
                                            filterDateFormat="ddd"
                                            fetched={fetched}
                                            fetching={fetching}
                                            graphTitle="revPAR by day of week"
                                            yAxisLabel="revPAR"
                                            lySpitPathToData="lySpit.stats.revPar.net"
                                            lyFinalPathToData="lyFinal.stats.revPar.net"
                                            budgetPathToData="budget.revPar"
                                            series={[
                                                {
                                                    pathToData: 'current.stats.revPar.net',
                                                    legendName: 'revPAR',
                                                },
                                            ]}
                                            chartType={CHART_TYPE.BAR}
                                            totalType={TOTAL_TYPE.AVERAGE}
                                        />
                                    ),
                                }}
                            />
                        </GraphContainer>
                    </Col>
                    <Col span="8">
                        <GraphContainer>
                            <Button
                                onClick={() => exportData(REVFLOW_REPORT.OCCUPANCY)}
                                loading={fetchingSpreadsheet}
                                disabled={fetching}
                                style={{ position: 'absolute', top: '20px', right: '20px', zIndex: '5' }}
                                size="small"
                            >
                                <DownloadOutlined />
                            </Button>
                            <FlipGraph
                                Graph1={{
                                    optionName: 'Month',
                                    graph: (
                                        <Graph
                                            key="occMonth"
                                            data={filteredData}
                                            possiblyAddFilter={possiblyAddFilter}
                                            removeFilter={removeFilter}
                                            filters={filters}
                                            grouping="MMM"
                                            xAxisType={X_AXIS_TYPE.DATE}
                                            yAxisType={Y_AXIS_TYPE.PERCENT}
                                            filterProperty="stayDate"
                                            filterDateFormat="MMM"
                                            fetched={fetched}
                                            fetching={fetching}
                                            graphTitle="Occupancy by month"
                                            yAxisLabel="Occupancy"
                                            lySpitPathToData="lySpit.stats.occupancy"
                                            lyFinalPathToData="lyFinal.stats.occupancy"
                                            budgetPathToData="budget.occupancy"
                                            series={[
                                                {
                                                    pathToData: 'current.stats.occupancy',
                                                    legendName: 'Occupancy',
                                                },
                                            ]}
                                            chartType={CHART_TYPE.BAR}
                                            totalType={TOTAL_TYPE.AVERAGE}
                                        />
                                    ),
                                }}
                                Graph2={{
                                    optionName: 'Day',
                                    graph: (
                                        <Graph
                                            key="occDayOfWeek"
                                            data={filteredData}
                                            possiblyAddFilter={possiblyAddFilter}
                                            removeFilter={removeFilter}
                                            filters={filters}
                                            grouping="ddd"
                                            xAxisType={X_AXIS_TYPE.DATE}
                                            yAxisType={Y_AXIS_TYPE.PERCENT}
                                            filterProperty="stayDate"
                                            filterDateFormat="ddd"
                                            fetched={fetched}
                                            fetching={fetching}
                                            graphTitle="Occupancy by day of week"
                                            yAxisLabel="Occupancy"
                                            lySpitPathToData="lySpit.stats.occupancy"
                                            lyFinalPathToData="lyFinal.stats.occupancy"
                                            budgetPathToData="budget.occupancy"
                                            series={[
                                                {
                                                    pathToData: 'current.stats.occupancy',
                                                    legendName: 'Occupancy',
                                                },
                                            ]}
                                            chartType={CHART_TYPE.BAR}
                                            totalType={TOTAL_TYPE.AVERAGE}
                                        />
                                    ),
                                }}
                            />
                        </GraphContainer>
                    </Col>
                    <Col span="8">
                        <GraphContainer>
                            <Button
                                onClick={() => exportData(REVFLOW_REPORT.ROOM_TYPES)}
                                loading={fetchingSpreadsheet}
                                disabled={fetching}
                                style={{ position: 'absolute', top: '20px', right: '20px', zIndex: '5' }}
                                size="small"
                            >
                                <DownloadOutlined />
                            </Button>
                            <Graph
                                key="roomBreakdown"
                                data={filteredData}
                                possiblyAddFilter={possiblyAddFilter}
                                removeFilter={removeFilter}
                                filters={filters}
                                grouping="roomTypes"
                                xAxisType={X_AXIS_TYPE.DATE}
                                filterProperty="roomTypeLookupHandle"
                                fetched={fetched}
                                fetching={fetching}
                                graphTitle="Room Types"
                                yAxisLabel="Rooms"
                                series={[
                                    {
                                        pathToData: 'current.stats.guaranteed.roomTypes',
                                        legendName: 'Room Types',
                                    },
                                ]}
                                chartType={CHART_TYPE.PIE}
                                groupOnResData
                            />
                        </GraphContainer>
                    </Col>
                </Row>
                <Row>
                    <Col span="8">
                        <GraphContainer>
                            <Button
                                onClick={() => exportData(REVFLOW_REPORT.RATE_CODES)}
                                loading={fetchingSpreadsheet}
                                disabled={fetching}
                                style={{ position: 'absolute', top: '20px', right: '20px', zIndex: '5' }}
                                size="small"
                            >
                                <DownloadOutlined />
                            </Button>
                            <Graph
                                key="rateBreakdown"
                                data={filteredData}
                                possiblyAddFilter={possiblyAddFilter}
                                removeFilter={removeFilter}
                                filters={filters}
                                grouping="rates"
                                xAxisType={X_AXIS_TYPE.DATE}
                                filterProperty="rateProfilePackageReference"
                                fetched={fetched}
                                fetching={fetching}
                                graphTitle="Rate codes"
                                yAxisLabel="Rooms"
                                series={[
                                    {
                                        pathToData: 'current.stats.guaranteed.rates',
                                        legendName: 'Rate code',
                                    },
                                ]}
                                chartType={CHART_TYPE.DOUGHNUT}
                                groupOnResData
                            />
                        </GraphContainer>
                    </Col>
                    <Col span="8">
                        <GraphContainer>
                            <Button
                                onClick={() => exportData(REVFLOW_REPORT.SOURCES)}
                                loading={fetchingSpreadsheet}
                                disabled={fetching}
                                style={{ position: 'absolute', top: '20px', right: '20px', zIndex: '5' }}
                                size="small"
                            >
                                <DownloadOutlined />
                            </Button>
                            <Graph
                                key="segmentSourceBreakdown"
                                data={filteredData}
                                possiblyAddFilter={possiblyAddFilter}
                                removeFilter={removeFilter}
                                filters={filters}
                                grouping="sources"
                                xAxisType={X_AXIS_TYPE.DATE}
                                filterProperty="segmentsSourceName"
                                fetched={fetched}
                                fetching={fetching}
                                graphTitle="Sources"
                                yAxisLabel="Rooms"
                                series={[
                                    {
                                        pathToData: 'current.stats.guaranteed.sources',
                                        legendName: 'Sources',
                                    },
                                ]}
                                chartType={CHART_TYPE.PIE}
                                groupOnResData
                            />
                        </GraphContainer>
                    </Col>
                    <Col span="8">
                        <GraphContainer>
                            <Button
                                onClick={() => exportData(REVFLOW_REPORT.SEGMENT_BUCKETS)}
                                loading={fetchingSpreadsheet}
                                disabled={fetching}
                                style={{ position: 'absolute', top: '20px', right: '20px', zIndex: '5' }}
                                size="small"
                            >
                                <DownloadOutlined />
                            </Button>
                            <Graph
                                key="segmentBucketBreakdown"
                                data={filteredData}
                                possiblyAddFilter={possiblyAddFilter}
                                removeFilter={removeFilter}
                                filters={filters}
                                grouping="segmentBuckets"
                                xAxisType={X_AXIS_TYPE.DATE}
                                filterProperty="bucketName"
                                fetched={fetched}
                                fetching={fetching}
                                graphTitle="Segment Buckets"
                                yAxisLabel="Rooms"
                                series={[
                                    {
                                        pathToData: 'current.stats.guaranteed.segmentBuckets',
                                        legendName: 'Buckets',
                                    },
                                ]}
                                chartType={CHART_TYPE.DOUGHNUT}
                                groupOnResData
                            />
                        </GraphContainer>
                    </Col>
                </Row>
                <Row>
                    <Col span="8">
                        <GraphContainer>
                            <Button
                                onClick={() => exportData(REVFLOW_REPORT.LOS)}
                                loading={fetchingSpreadsheet}
                                disabled={fetching}
                                style={{ position: 'absolute', top: '20px', right: '20px', zIndex: '5' }}
                                size="small"
                            >
                                <DownloadOutlined />
                            </Button>
                            <Graph
                                key="lengthOfStayBreakdown"
                                data={filteredData}
                                possiblyAddFilter={possiblyAddFilter}
                                removeFilter={removeFilter}
                                filters={filters}
                                grouping="lengthOfStays"
                                xAxisType={X_AXIS_TYPE.NUMBER}
                                filterProperty="lengthOfStay"
                                filterLabel="LOS"
                                fetched={fetched}
                                fetching={fetching}
                                graphTitle="Length of Stay"
                                yAxisLabel="Rooms"
                                lyFinalPathToData="lyFinal.stats.guaranteed.lengthOfStays"
                                series={[
                                    {
                                        pathToData: 'current.stats.guaranteed.lengthOfStays',
                                        legendName: 'Room Nights',
                                    },
                                ]}
                                chartType={CHART_TYPE.BAR}
                                groupOnResData
                            />
                        </GraphContainer>
                    </Col>
                </Row>
            </Container>
        </>
    );
};

RevFlow.propTypes = {};

export default RevFlow;
